import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, MagnifyingGlass, Polygon, Trash, FloppyDisk, FilePdf, Ruler, Calculator, MapPin, Lightning, SpinnerGap } from '@phosphor-icons/react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { API_URL } from '../config/api';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Load Google Maps script
const loadGoogleMaps = () => {
  return new Promise((resolve, reject) => {
    if (window.google?.maps) { resolve(window.google.maps); return; }
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) { existing.addEventListener('load', () => resolve(window.google.maps)); return; }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=drawing,geometry,places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error('Google Maps failed to load'));
    document.head.appendChild(script);
  });
};

const sqFtToAcres = (sqft) => (sqft / 43560).toFixed(3);
const sqFtToSqYd = (sqft) => (sqft / 9).toFixed(1);

export const PropertyMeasurePage = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const drawingManager = useRef(null);
  const polygonsRef = useRef([]);
  const geocoderRef = useRef(null);

  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [address, setAddress] = useState('');
  const [searching, setSearching] = useState(false);
  const [zones, setZones] = useState([]);
  const [activeZoneIdx, setActiveZoneIdx] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [savedMeasurements, setSavedMeasurements] = useState([]);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    service_type: 'leveling',
    depth_inches: 1,
    rate_per_sqft: 0.15,
    rate_per_cuyd: 45,
    notes: '',
  });
  const [propertyName, setPropertyName] = useState('');
  const [aiScanning, setAiScanning] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  // Live area readout shown on map during polygon edits (accuracy self-correction)
  const [liveStats, setLiveStats] = useState(null); // { name, sqft, linearFt, zoneIdx } | null
  const liveStatsTimeoutRef = useRef(null);

  // Load Google Maps
  useEffect(() => {
    loadGoogleMaps().then((maps) => {
      setMapsLoaded(true);
      // Init map
      const map = new maps.Map(mapRef.current, {
        center: { lat: 30.6335, lng: -97.6779 }, // Georgetown TX default
        zoom: 19,
        mapTypeId: 'satellite',
        tilt: 0,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });
      mapInstance.current = map;
      geocoderRef.current = new maps.Geocoder();

      // Drawing manager
      const dm = new maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: false,
        polygonOptions: {
          fillColor: '#0D9256',
          fillOpacity: 0.25,
          strokeColor: '#0D9256',
          strokeWeight: 2,
          editable: true,
          draggable: true,
        },
      });
      dm.setMap(map);
      drawingManager.current = dm;

      // When polygon is completed
      maps.event.addListener(dm, 'polygoncomplete', (polygon) => {
        const area = maps.geometry.spherical.computeArea(polygon.getPath());
        const sqft = area * 10.7639; // m² to ft²
        const perimeter = maps.geometry.spherical.computeLength(polygon.getPath());
        const linearFt = perimeter * 3.28084;

        polygonsRef.current.push(polygon);
        setZones((prev) => {
          const newZone = {
            id: `zone-${Date.now()}`,
            name: `Zone ${prev.length + 1}`,
            sqft: Math.round(sqft),
            linearFt: Math.round(linearFt),
            type: 'lawn',
          };
          return [...prev, newZone];
        });
        setActiveZoneIdx(polygonsRef.current.length - 1);
        dm.setDrawingMode(null);
        setIsDrawing(false);

        // Update area on edit — live stats drive both sidebar and on-map readout
        const updateArea = () => {
          const newArea = maps.geometry.spherical.computeArea(polygon.getPath());
          const newSqft = Math.round(newArea * 10.7639);
          const newPerimeter = maps.geometry.spherical.computeLength(polygon.getPath());
          const newLinFt = Math.round(newPerimeter * 3.28084);
          const zoneIdx = polygonsRef.current.indexOf(polygon);
          setZones((prev) => prev.map((z, i) =>
            i === zoneIdx ? { ...z, sqft: newSqft, linearFt: newLinFt } : z
          ));
          // Surface a floating badge with the latest numbers while user drags
          setZones((prev) => {
            const z = prev[zoneIdx];
            setLiveStats({
              name: z?.name || `Zone ${zoneIdx + 1}`,
              sqft: newSqft,
              linearFt: newLinFt,
              zoneIdx,
            });
            return prev;
          });
          if (liveStatsTimeoutRef.current) clearTimeout(liveStatsTimeoutRef.current);
          liveStatsTimeoutRef.current = setTimeout(() => setLiveStats(null), 2500);
        };
        polygon.getPath().addListener('set_at', updateArea);
        polygon.getPath().addListener('insert_at', updateArea);
        polygon.getPath().addListener('remove_at', updateArea);
        polygon.addListener('dragend', updateArea);

        // Click to select
        polygon.addListener('click', () => {
          const idx = polygonsRef.current.indexOf(polygon);
          setActiveZoneIdx(idx);
        });
      });
    }).catch((err) => {
      toast.error('Failed to load Google Maps');
      console.error(err);
    });
  }, []);

  // Search address
  const handleSearch = useCallback(() => {
    if (!address.trim() || !geocoderRef.current) return;
    setSearching(true);
    geocoderRef.current.geocode({ address: address.trim() }, (results, status) => {
      setSearching(false);
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        mapInstance.current.setCenter(loc);
        mapInstance.current.setZoom(20);
        setPropertyName(results[0].formatted_address);
        toast.success('Property found');
      } else {
        console.error('Geocode failed:', status);
        // Fallback: try using Places text search if Geocoding fails
        if (status === 'REQUEST_DENIED' || status === 'OVER_QUERY_LIMIT') {
          toast.error('Geocoding API not available — check Google Cloud billing and API restrictions');
        } else {
          toast.error(`Address not found (${status}) — try a different format`);
        }
      }
    });
  }, [address]);

  // Auto-populate + search when arriving from a Client Property card (URL ?address=&client=)
  useEffect(() => {
    if (!mapsLoaded) return;
    const params = new URLSearchParams(window.location.search);
    const addr = params.get('address');
    const client = params.get('client');
    if (addr) {
      setAddress(addr);
      if (client) setPropertyName(`${client} — ${addr}`);
      // Trigger search after state settles
      setTimeout(() => {
        if (geocoderRef.current) {
          setSearching(true);
          geocoderRef.current.geocode({ address: addr }, (results, status) => {
            setSearching(false);
            if (status === 'OK' && results[0]) {
              const loc = results[0].geometry.location;
              mapInstance.current.setCenter(loc);
              mapInstance.current.setZoom(20);
              toast.success(`Loaded: ${client || addr}`);
            }
          });
        }
      }, 300);
    }
  }, [mapsLoaded]);

  // Start drawing
  const startDrawing = () => {
    if (!drawingManager.current) return;
    // Auto-zoom to 21 for max pixel precision when drawing (zoom 20 was source of ~4.5% polygon error)
    if (mapInstance.current) {
      const currentZoom = mapInstance.current.getZoom();
      if (currentZoom < 21) {
        mapInstance.current.setZoom(21);
      }
    }
    drawingManager.current.setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);
    setIsDrawing(true);
    toast('Click points around the zone edge. Tight corners = better accuracy. Click first point to close.', { duration: 5000 });
  };

  // Delete zone
  const deleteZone = (idx) => {
    polygonsRef.current[idx]?.setMap(null);
    polygonsRef.current.splice(idx, 1);
    setZones((prev) => prev.filter((_, i) => i !== idx));
    setActiveZoneIdx(null);
  };

  // AI Scan — capture satellite view and analyze
  const handleAiScan = async () => {
    if (!mapInstance.current) { toast.error('Map not ready'); return; }
    setAiScanning(true);
    setAiAnalysis(null);
    toast('Capturing satellite view for AI analysis...', { duration: 3000 });

    try {
      // Get precise map bounds and dimensions
      const bounds = mapInstance.current.getBounds();
      const center = mapInstance.current.getCenter();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const zoom = mapInstance.current.getZoom();
      const mapDiv = mapRef.current;
      const pixelWidth = mapDiv.offsetWidth;
      const pixelHeight = mapDiv.offsetHeight;

      // Calculate exact view area in sq ft using Google's geometry library
      const viewCorners = [
        new window.google.maps.LatLng(ne.lat(), sw.lng()), // NW
        new window.google.maps.LatLng(ne.lat(), ne.lng()), // NE
        new window.google.maps.LatLng(sw.lat(), ne.lng()), // SE
        new window.google.maps.LatLng(sw.lat(), sw.lng()), // SW
      ];
      const viewAreaM2 = window.google.maps.geometry.spherical.computeArea(viewCorners);
      const viewAreaSqft = Math.round(viewAreaM2 * 10.7639);

      // Capture map using html2canvas (best effort — tiles may be partial)
      let imageBase64 = '';
      try {
        const canvas = await html2canvas(mapDiv, {
          useCORS: true,
          allowTaint: true,
          scale: 1,
          logging: false,
          backgroundColor: null,
        });
        imageBase64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
      } catch {
        toast.error('Could not capture map image');
      }

      toast('Analyzing property with AI...', { duration: 5000 });

      const res = await axios.post(`${API_URL}/api/measurements/ai-scan`, {
        image_base64: imageBase64,
        bounds: {
          ne: { lat: ne.lat(), lng: ne.lng() },
          sw: { lat: sw.lat(), lng: sw.lng() },
        },
        center: { lat: center.lat(), lng: center.lng() },
        zoom: zoom,
        view_area_sqft: viewAreaSqft,
        pixel_dimensions: { width: pixelWidth, height: pixelHeight },
        address: address || propertyName || '',
      }, { withCredentials: true, timeout: 60000 });

      setAiAnalysis(res.data);

      // Add a subtle center marker to indicate analyzed property
      if (mapInstance.current) {
        new window.google.maps.Marker({
          position: mapInstance.current.getCenter(),
          map: mapInstance.current,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: '#0D9256',
            fillOpacity: 0.3,
            strokeColor: '#0D9256',
            strokeWeight: 2,
          },
          title: 'Analyzed property',
        });
      }

      if (res.data.zones?.length > 0) {
        toast.success(`AI detected ${res.data.zones.length} zones — draw polygons for exact measurements`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'AI scan failed — try again';
      toast.error(msg);
    } finally {
      setAiScanning(false);
    }
  };

  // Highlight active zone
  useEffect(() => {
    polygonsRef.current.forEach((p, i) => {
      p.setOptions({
        strokeColor: i === activeZoneIdx ? '#FACC15' : '#0D9256',
        strokeWeight: i === activeZoneIdx ? 3 : 2,
        fillOpacity: i === activeZoneIdx ? 0.35 : 0.25,
      });
    });
  }, [activeZoneIdx]);

  // Totals
  const totalSqft = zones.reduce((s, z) => s + z.sqft, 0);
  const totalLinearFt = zones.reduce((s, z) => s + z.linearFt, 0);
  const lawnZones = zones.filter((z) => z.type === 'lawn');
  const totalLawnSqft = lawnZones.reduce((s, z) => s + z.sqft, 0);

  // Quote calculation
  const cubicYards = (totalLawnSqft * (quoteForm.depth_inches / 12)) / 27;
  const materialCost = cubicYards * quoteForm.rate_per_cuyd;
  const laborCost = totalLawnSqft * quoteForm.rate_per_sqft;
  const totalQuote = materialCost + laborCost;

  // Save measurement
  const saveMeasurement = async () => {
    if (zones.length === 0) { toast.error('Draw at least one zone first'); return; }
    try {
      const data = {
        property_name: propertyName || address || 'Unnamed Property',
        address: address,
        zones: zones.map((z) => ({ ...z })),
        total_sqft: totalSqft,
        total_linear_ft: totalLinearFt,
      };
      await axios.post(`${API_URL}/api/measurements`, data, { withCredentials: true });
      toast.success('Measurement saved');
      fetchSavedMeasurements();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  // Fetch saved
  const fetchSavedMeasurements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/measurements`, { withCredentials: true });
      setSavedMeasurements(res.data);
    } catch {}
  };

  useEffect(() => { fetchSavedMeasurements(); }, []);

  // Generate quote PDF
  const generateQuote = async () => {
    try {
      const data = {
        property_name: propertyName || address || 'Property',
        address: address,
        zones,
        total_sqft: totalSqft,
        total_lawn_sqft: totalLawnSqft,
        total_linear_ft: totalLinearFt,
        service_type: quoteForm.service_type,
        depth_inches: quoteForm.depth_inches,
        rate_per_cuyd: quoteForm.rate_per_cuyd,
        rate_per_sqft: quoteForm.rate_per_sqft,
        cubic_yards: Math.round(cubicYards * 10) / 10,
        material_cost: Math.round(materialCost * 100) / 100,
        labor_cost: Math.round(laborCost * 100) / 100,
        total_quote: Math.round(totalQuote * 100) / 100,
        notes: quoteForm.notes,
      };
      const res = await axios.post(`${API_URL}/api/measurements/quote-pdf`, data, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      toast.success('Quote PDF generated');
      setShowQuoteDialog(false);
    } catch (e) {
      toast.error('Quote generation failed');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="property-measure-page">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur sticky top-0 z-20">
        <div className="max-w-[1800px] mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="measure-back-btn">
            <ArrowLeft size={16} />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold tracking-tight">Property Measure</h1>
            <p className="text-xs text-muted-foreground">Satellite measurement & quoting tool</p>
          </div>
          {zones.length > 0 && (
            <div className="hidden md:flex items-center gap-4 text-sm">
              <span className="font-mono font-bold text-primary">{totalSqft.toLocaleString()} sq ft</span>
              <span className="text-muted-foreground">({sqFtToAcres(totalSqft)} acres)</span>
              <span className="text-muted-foreground">{totalLinearFt.toLocaleString()} linear ft</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-[1800px] mx-auto flex flex-col lg:flex-row h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <div className="w-full lg:w-80 xl:w-96 border-r bg-card overflow-y-auto p-4 space-y-4 shrink-0">
          {/* Address Search */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Property Address</Label>
            <div className="flex gap-1.5 mt-1">
              <Input
                placeholder="Enter address..."
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="measure-address-input"
              />
              <Button size="sm" onClick={handleSearch} disabled={searching} data-testid="measure-search-btn">
                {searching ? <span className="animate-spin">...</span> : <MagnifyingGlass size={16} />}
              </Button>
            </div>
          </div>

          {/* Drawing Controls */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={isDrawing ? 'destructive' : 'default'}
              className="flex-1 gap-1"
              onClick={() => isDrawing ? (drawingManager.current?.setDrawingMode(null), setIsDrawing(false)) : startDrawing()}
              data-testid="measure-draw-btn"
            >
              <Polygon size={14} />
              {isDrawing ? 'Cancel Drawing' : 'Draw Zone'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 gap-1 border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
              onClick={handleAiScan}
              disabled={aiScanning}
              data-testid="measure-ai-scan-btn"
            >
              {aiScanning ? <SpinnerGap size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
              {aiScanning ? 'Scanning...' : 'AI Scan'}
            </Button>
          </div>
          <div className="flex gap-2">
            {zones.length > 0 && (
              <>
                <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={saveMeasurement} data-testid="measure-save-btn">
                  <FloppyDisk size={14} /> Save
                </Button>
                <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => setShowQuoteDialog(true)} data-testid="measure-quote-btn">
                  <Calculator size={14} /> Quote
                </Button>
              </>
            )}
          </div>

          {/* AI Analysis Results */}
          {aiAnalysis && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="ai-analysis-panel">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider flex items-center gap-1">
                  <Lightning size={12} weight="fill" /> AI Property Analysis
                </p>
                <button className="text-[10px] text-muted-foreground hover:text-foreground" onClick={() => setAiAnalysis(null)}>dismiss</button>
              </div>
              {aiAnalysis.property_boundary_description && (
                <p className="text-[11px] text-muted-foreground italic border-l-2 border-amber-500/40 pl-2">
                  {aiAnalysis.property_boundary_description}
                </p>
              )}
              {aiAnalysis.property_summary && (
                <p className="text-xs text-muted-foreground">{aiAnalysis.property_summary}</p>
              )}
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <div className="bg-background rounded px-2 py-1.5 border">
                  <span className="text-muted-foreground block text-[10px]">Lawn Area (incl. under trees)</span>
                  <span className="font-mono font-bold text-primary">{(aiAnalysis.total_lawn_sqft || 0).toLocaleString()} ft²</span>
                  {aiAnalysis.tree_canopy_over_lawn_sqft > 0 && (
                    <span className="block text-[9px] text-emerald-600 mt-0.5">
                      +{aiAnalysis.tree_canopy_over_lawn_sqft.toLocaleString()} ft² under tree canopy
                    </span>
                  )}
                </div>
                <div className="bg-background rounded px-2 py-1.5 border">
                  <span className="text-muted-foreground block text-[10px]">Hardscape</span>
                  <span className="font-mono font-bold">{(aiAnalysis.total_hardscape_sqft || 0).toLocaleString()} ft²</span>
                </div>
              </div>
              {aiAnalysis.zones?.map((z, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-background rounded px-2 py-1.5 border">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${
                    z.type === 'lawn' ? 'bg-green-500' :
                    z.type === 'driveway' || z.type === 'sidewalk' ? 'bg-gray-400' :
                    z.type === 'house' ? 'bg-violet-500' :
                    z.type === 'flower_bed' || z.type === 'garden' ? 'bg-amber-500' :
                    z.type === 'pool' ? 'bg-sky-500' :
                    z.type === 'tree_canopy' ? 'bg-emerald-700' : 'bg-gray-300'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{z.name}</span>
                    {z.condition && z.condition !== 'n/a' && <span className="text-muted-foreground ml-1">({z.condition})</span>}
                  </div>
                  <span className="font-mono font-medium shrink-0">{(z.est_sqft || 0).toLocaleString()} ft²</span>
                </div>
              ))}
              {aiAnalysis.recommendations?.length > 0 && (
                <div className="text-[10px] text-muted-foreground border-t border-amber-500/20 pt-1.5 mt-1">
                  {aiAnalysis.recommendations.map((r, i) => <p key={i}>• {r}</p>)}
                </div>
              )}
              <p className="text-[9px] text-muted-foreground/60 italic pt-1 border-t border-amber-500/10">
                {aiAnalysis.accuracy_note || 'AI estimate — draw polygons for exact, billable measurements.'}
              </p>
            </div>
          )}

          {/* Zone List */}
          {zones.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Zones ({zones.length})</p>
              {zones.map((z, i) => (
                <div
                  key={z.id}
                  className={`rounded-lg border p-3 cursor-pointer transition-colors ${i === activeZoneIdx ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}
                  onClick={() => setActiveZoneIdx(i)}
                  data-testid={`measure-zone-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <input
                      className="text-sm font-medium bg-transparent border-none outline-none w-24"
                      value={z.name}
                      onChange={(e) => setZones((prev) => prev.map((zone, idx) => idx === i ? { ...zone, name: e.target.value } : zone))}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex items-center gap-1">
                      <Select
                        value={z.type}
                        onValueChange={(v) => setZones((prev) => prev.map((zone, idx) => idx === i ? { ...zone, type: v } : zone))}
                      >
                        <SelectTrigger className="h-6 text-[10px] w-20 border-none">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lawn">Lawn</SelectItem>
                          <SelectItem value="bed">Bed</SelectItem>
                          <SelectItem value="hardscape">Hardscape</SelectItem>
                          <SelectItem value="driveway">Driveway</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={(e) => { e.stopPropagation(); deleteZone(i); }}>
                        <Trash size={12} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-1.5 text-xs text-muted-foreground">
                    <span className="font-mono font-medium text-foreground">{z.sqft.toLocaleString()} ft²</span>
                    <span>{sqFtToSqYd(z.sqft)} yd²</span>
                    <span>{z.linearFt} lin ft</span>
                  </div>
                </div>
              ))}

              {/* Totals */}
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-3">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">Totals</p>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-muted-foreground">Total Area</span>
                  <span className="font-mono font-bold text-right">{totalSqft.toLocaleString()} ft²</span>
                  <span className="text-muted-foreground">Lawn Area</span>
                  <span className="font-mono font-medium text-right">{totalLawnSqft.toLocaleString()} ft²</span>
                  <span className="text-muted-foreground">Acres</span>
                  <span className="font-mono text-right">{sqFtToAcres(totalSqft)}</span>
                  <span className="text-muted-foreground">Perimeter</span>
                  <span className="font-mono text-right">{totalLinearFt.toLocaleString()} lin ft</span>
                </div>
              </div>
            </div>
          )}

          {/* Saved Measurements */}
          {savedMeasurements.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recent Measurements</p>
              <div className="space-y-1.5">
                {savedMeasurements.slice(0, 5).map((m, i) => (
                  <div key={i} className="text-xs p-2 rounded border border-border/50 bg-muted/30">
                    <p className="font-medium truncate">{m.property_name}</p>
                    <div className="flex justify-between mt-0.5 text-muted-foreground">
                      <span>{m.total_sqft?.toLocaleString()} ft²</span>
                      <span>{m.zones?.length || 0} zones</span>
                      <span>{new Date(m.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {zones.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm font-medium">Search an address, then draw zones</p>
              <p className="text-xs mt-1">Click "Draw Zone" to outline lawn areas on the satellite view</p>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="flex-1 relative">
          <div ref={mapRef} className="w-full h-full min-h-[400px]" data-testid="measure-map" />
          {!mapsLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-background">
              <p className="text-muted-foreground animate-pulse">Loading satellite view...</p>
            </div>
          )}
          {/* Live area readout — appears while dragging polygon vertices for in-context accuracy feedback */}
          {liveStats && (
            <div
              className="absolute top-4 right-4 z-20 bg-card/95 backdrop-blur-sm border-2 border-primary shadow-lg rounded-lg px-4 py-3 animate-in fade-in-0 slide-in-from-top-2 duration-200 pointer-events-none"
              data-testid="measure-live-stats"
            >
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Editing</p>
              <p className="text-sm font-semibold text-foreground truncate max-w-[200px]">{liveStats.name}</p>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-bold text-primary font-mono">{liveStats.sqft.toLocaleString()}</span>
                <span className="text-xs text-muted-foreground">sq ft</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {liveStats.linearFt.toLocaleString()} linear ft · {sqFtToAcres(liveStats.sqft)} ac
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quote Dialog */}
      <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator size={18} />
              Generate Quote
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-muted/50 p-3 text-xs space-y-1">
              <div className="flex justify-between"><span>Property</span><span className="font-medium truncate ml-2">{propertyName || address || 'N/A'}</span></div>
              <div className="flex justify-between"><span>Total Lawn Area</span><span className="font-mono font-bold">{totalLawnSqft.toLocaleString()} ft²</span></div>
              <div className="flex justify-between"><span>Zones</span><span>{zones.length}</span></div>
            </div>

            <div>
              <Label className="text-xs">Service Type</Label>
              <Select value={quoteForm.service_type} onValueChange={(v) => setQuoteForm({ ...quoteForm, service_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="leveling">Lawn Leveling</SelectItem>
                  <SelectItem value="mowing">Mowing</SelectItem>
                  <SelectItem value="fertilization">Fertilization</SelectItem>
                  <SelectItem value="aeration">Aeration</SelectItem>
                  <SelectItem value="overseeding">Overseeding</SelectItem>
                  <SelectItem value="sod_install">Sod Installation</SelectItem>
                  <SelectItem value="custom">Custom Service</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {quoteForm.service_type === 'leveling' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Fill Depth (inches)</Label>
                  <Input type="number" step="0.25" min="0.25" value={quoteForm.depth_inches} onChange={(e) => setQuoteForm({ ...quoteForm, depth_inches: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label className="text-xs">Rate per Cu/Yd ($)</Label>
                  <Input type="number" step="1" value={quoteForm.rate_per_cuyd} onChange={(e) => setQuoteForm({ ...quoteForm, rate_per_cuyd: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">Labor Rate (per sq ft $)</Label>
              <Input type="number" step="0.01" value={quoteForm.rate_per_sqft} onChange={(e) => setQuoteForm({ ...quoteForm, rate_per_sqft: parseFloat(e.target.value) || 0 })} />
            </div>

            <div>
              <Label className="text-xs">Notes</Label>
              <Input placeholder="Special conditions, access notes..." value={quoteForm.notes} onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })} />
            </div>

            {/* Quote Summary */}
            <div className="rounded-lg bg-primary/10 border border-primary/20 p-3 space-y-1.5">
              <p className="text-xs font-semibold text-primary uppercase tracking-wider">Quote Summary</p>
              {quoteForm.service_type === 'leveling' && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Material ({cubicYards.toFixed(1)} cu yd × ${quoteForm.rate_per_cuyd})</span>
                  <span className="font-mono">${materialCost.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Labor ({totalLawnSqft.toLocaleString()} ft² × ${quoteForm.rate_per_sqft})</span>
                <span className="font-mono">${laborCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t border-primary/20 pt-1.5 mt-1.5">
                <span>Total</span>
                <span className="text-primary font-mono">${totalQuote.toFixed(2)}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button className="flex-1" onClick={generateQuote} data-testid="generate-quote-btn">
                Generate Quote
              </Button>
              <Button variant="outline" onClick={() => setShowQuoteDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyMeasurePage;
